import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const FAQS = [
  { q: 'What is the best CUET study plan?', a: 'The best plan balances concept building, daily practice, regular revision, and mock testing across a realistic timeline — the 30-day structure on this page (foundation, practice, accuracy, final revision) works well as a template.' },
  { q: 'Is a 30-day CUET study plan enough?', a: 'It can be, for focused final-stretch preparation, especially for students who already have some concept familiarity. Students starting from scratch generally benefit from a longer runway, using the same weekly structure stretched across more time.' },
  { q: 'How many hours should I study daily for CUET?', a: 'A consistent 6–8 hour day (spread across the sample timetable) tends to work well during an intensive 30-day phase; less during earlier, longer-term preparation.' },
  { q: 'How should beginners prepare for CUET?', a: 'Start with Week 1 of this plan — syllabus understanding and NCERT-based concept building — before moving into topic-wise practice and mock tests.' },
  { q: 'Can I prepare for CUET in one month?', a: 'Yes, particularly for focused revision if concepts are already reasonably solid. A 30-day plan works best when it is the final phase of preparation, not the entire preparation window.' },
  { q: 'How do I balance board exams and CUET preparation?', a: 'Build a timetable that allocates specific blocks to each, and adjust the daily schedule around actual school or board exam commitments rather than treating them as separate tracks.' },
  { q: 'Should I study every subject every day?', a: 'Not necessarily every subject in equal depth every day, but touching each subject regularly (even briefly) prevents the kind of neglect that leads to weak areas late in preparation.' },
  { q: 'How important are mock tests in a study plan?', a: 'Very — Weeks 3 and 4 of this plan are built around them specifically, since they convert content knowledge into exam-day performance.' },
  { q: 'What should I do in the last week before CUET?', a: "Focus on full-length mocks, quick revision, and consolidating what's already been learned — avoid introducing new topics this close to the exam." },
  { q: 'Which books should I follow for CUET?', a: 'NCERT textbooks remain the core resource, supplemented with previous year papers and structured practice material.' },
  { q: 'Does BESST provide a structured CUET study plan?', a: "Yes, BESST designs personalised study plans for students as part of its CUET Online Coaching programme." },
  { q: "Are live classes included in BESST's CUET Online Coaching?", a: 'Yes, live interactive classes are a core part of the programme, alongside recorded sessions for revision.' },
  { q: 'Does BESST provide mock tests and study materials?', a: 'Yes, both are included, aligned to the structured study plan each student follows.' },
  { q: 'Can I customise my study plan?', a: "Yes — the 30-day structure here is a template. BESST's personalised plans adjust the weekly focus and daily timetable based on each student's actual starting point and progress." },
  { q: 'How do I stay consistent during CUET preparation?', a: 'Set realistic daily goals, track progress visibly, take regular mock tests, and prioritise rest alongside study — consistency holds up better than intensity over a full preparation cycle.' },
];

const WEEKS = [
  {
    week: 'Week 1',
    theme: 'Build Your Foundation',
    color: '#7c3aed',
    bg: 'rgba(124,58,237,.12)',
    border: 'rgba(124,58,237,.3)',
    icon: '🧱',
    items: ['Understand the full syllabus section by section', 'Revise core NCERT concepts for all domain subjects', 'Prepare structured notes as you go', 'Solve basic practice questions after each topic'],
    goal: 'By end of Week 1: Syllabus fully mapped, NCERT first pass complete, notes in hand.',
  },
  {
    week: 'Week 2',
    theme: 'Strengthen Concepts',
    color: '#2563eb',
    bg: 'rgba(37,99,235,.12)',
    border: 'rgba(37,99,235,.3)',
    icon: '💪',
    items: ['Topic-wise focused practice for every subject', 'Daily short revision of Week 1 notes', 'Subject-wise timed tests after each topic batch', 'Begin practising within actual section time limits'],
    goal: 'By end of Week 2: Every topic practised, first set of timed tests completed.',
  },
  {
    week: 'Week 3',
    theme: 'Improve Accuracy',
    color: '#d97706',
    bg: 'rgba(217,119,6,.12)',
    border: 'rgba(217,119,6,.3)',
    icon: '🎯',
    items: ['Solve previous year question papers daily', 'Take sectional mock tests (one per section per day)', 'Analyse every mistake — why, not just which', 'Target and improve the weakest three topics'],
    goal: 'By end of Week 3: PYQs done, at least 2 full sectional mocks per section, weak areas identified and actively addressed.',
  },
  {
    week: 'Week 4',
    theme: 'Final Revision & Exam Readiness',
    color: '#16a34a',
    bg: 'rgba(22,163,74,.12)',
    border: 'rgba(22,163,74,.3)',
    icon: '🏁',
    items: ['Two to three full-length mock tests in exam conditions', 'Quick revision of all formulae, key facts, and notes', 'Current affairs final review', 'Exam strategy: section order, time allocation, skip/attempt decisions'],
    goal: 'By end of Week 4: Full exam familiarity, weak areas revisited, ready to walk in.',
  },
];

const TIMETABLE = [
  { time: '6:30–7:30 AM', activity: 'Revision', note: 'Previous day\'s notes or weak topics', type: 'revision' },
  { time: '8:00–10:00 AM', activity: 'Subject 1', note: 'Primary domain subject — high-intensity block', type: 'study' },
  { time: '10:30–12:00 PM', activity: 'Subject 2', note: 'Second domain subject or language section', type: 'study' },
  { time: '12:00–2:00 PM', activity: 'Break', note: 'Lunch and rest — non-negotiable', type: 'break' },
  { time: '2:00–3:00 PM', activity: 'Practice Questions', note: 'Topic-wise sets on the morning\'s material', type: 'practice' },
  { time: '4:00–5:00 PM', activity: 'General Test', note: 'Aptitude, reasoning, GK, or current affairs in rotation', type: 'study' },
  { time: '6:00–7:00 PM', activity: 'Mock Test Analysis', note: 'Review results from the week\'s mock — specific to test days', type: 'analysis' },
  { time: '8:00–9:00 PM', activity: 'Revision & Notes', note: 'Consolidate the day, update notes, flag tomorrow\'s focus', type: 'revision' },
];

const BLOCK_COLORS = {
  revision: { bg: 'rgba(124,58,237,.1)', border: 'rgba(124,58,237,.25)', dot: '#7c3aed' },
  study:    { bg: 'rgba(37,99,235,.1)',  border: 'rgba(37,99,235,.25)',  dot: '#2563eb' },
  practice: { bg: 'rgba(217,119,6,.1)', border: 'rgba(217,119,6,.25)', dot: '#d97706' },
  analysis: { bg: 'rgba(22,163,74,.1)', border: 'rgba(22,163,74,.25)', dot: '#16a34a' },
  break:    { bg: 'rgba(100,116,139,.08)', border: 'rgba(100,116,139,.2)', dot: '#64748b' },
};

const BESST_FEATURES = [
  { icon: '🗓️', title: 'Expert-Designed Study Plans', desc: "Personalised schedules built around a student's actual starting point and timeline, not a generic template." },
  { icon: '📡', title: 'Live Interactive Classes', desc: 'Real-time teaching that keeps the foundation and concept-building weeks on track.' },
  { icon: '✏️', title: 'Daily Practice Sessions', desc: 'Structured practice that mirrors the "Practice Questions" and "Subject" blocks in the sample timetable.' },
  { icon: '📋', title: 'Topic-wise Assignments', desc: 'Reinforcement exercises tied directly to whatever was covered in that week\'s classes.' },
  { icon: '📝', title: 'Mock Test Series', desc: 'A scheduled cycle of sectional and full-length mocks aligned to Weeks 3 and 4 of the plan.' },
  { icon: '📊', title: 'Performance Tracking', desc: 'Analytics after every test, so "Performance Analysis" in Week 3 becomes a concrete report, not a guess.' },
  { icon: '🤝', title: 'Personalised Mentorship', desc: "One-to-one support to adjust the plan when a student's actual pace differs from the template." },
  { icon: '💬', title: 'Doubt Resolution', desc: "Dedicated sessions so unresolved doubts don't derail the following week's schedule." },
  { icon: '🎬', title: 'Recorded Lectures', desc: "A safety net for any day the daily timetable slips — nothing is lost if a session is missed." },
];

const TOC = [
  { id: 'why-need', label: 'Why You Need a Study Plan' },
  { id: 'before-start', label: 'Before You Start' },
  { id: 'thirty-day', label: '30-Day CUET Study Plan' },
  { id: 'daily-timetable', label: 'Daily Timetable' },
  { id: 'subject-wise', label: 'Subject-wise Plan' },
  { id: 'weekly-goals', label: 'Weekly Goals' },
  { id: 'mistakes', label: 'Common Mistakes' },
  { id: 'resources', label: 'Study Resources' },
  { id: 'besst-helps', label: 'How BESST Helps' },
  { id: 'comparison', label: 'Self-Study vs Coaching' },
  { id: 'expert-tips', label: 'Expert Tips' },
  { id: 'faqs', label: 'FAQs' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://besst.in' },
    { '@type': 'ListItem', position: 2, name: 'CUET Coaching', item: 'https://besst.in/cuet-coaching' },
    { '@type': 'ListItem', position: 3, name: 'CUET Study Plan', item: 'https://besst.in/cuet-study-plan' },
  ],
};

const CSS = `
  .csp-page{background:linear-gradient(160deg,#0c0420 0%,#140831 50%,#0c0420 100%);min-height:100vh;color:#e8deff;font-family:'Inter','Segoe UI',system-ui,sans-serif;}
  .csp-bc{max-width:1200px;margin:0 auto;padding:1rem 1.5rem 0;font-size:.8rem;color:#9ca3af;display:flex;align-items:center;gap:.4rem;flex-wrap:wrap;}
  .csp-bc a{color:#a78bfa;text-decoration:none;}.csp-bc a:hover{color:#d4af37;}
  .csp-hero{padding:3rem 1.5rem 3.5rem;max-width:860px;margin:0 auto;text-align:center;}
  .csp-badge{display:inline-block;background:rgba(212,175,55,.12);color:#d4af37;border:1px solid rgba(212,175,55,.3);border-radius:20px;padding:.35rem 1rem;font-size:.78rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:1.25rem;}
  .csp-h1{font-size:clamp(1.75rem,4vw,2.7rem);font-weight:800;line-height:1.2;margin:0 0 1.25rem;color:#f0e6ff;}
  .csp-h1 em{font-style:normal;background:linear-gradient(90deg,#d4af37,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .csp-intro{font-size:1.05rem;line-height:1.8;color:#c4b5fd;max-width:720px;margin:0 auto 2rem;}
  .csp-cta{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}
  .btn-gold{background:linear-gradient(135deg,#d4af37,#f59e0b);color:#0c0420;font-weight:700;padding:.85rem 2rem;border-radius:10px;border:none;cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:transform .2s,box-shadow .2s;}
  .btn-gold:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(212,175,55,.35);}
  .btn-outline{background:transparent;color:#e8deff;font-weight:600;padding:.85rem 2rem;border-radius:10px;border:1.5px solid rgba(232,222,255,.25);cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:border-color .2s,background .2s;}
  .btn-outline:hover{border-color:#a78bfa;background:rgba(167,139,250,.08);}
  .csp-qa{background:rgba(212,175,55,.07);border:1px solid rgba(212,175,55,.2);border-radius:12px;padding:1.25rem 1.5rem;margin-bottom:2rem;}
  .csp-qa-lbl{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#d4af37;margin-bottom:.5rem;}
  .csp-qa-txt{font-size:.93rem;line-height:1.75;color:#c4b5fd;}
  .csp-layout{max-width:1200px;margin:0 auto;padding:3rem 1.5rem;display:grid;grid-template-columns:220px 1fr;gap:3rem;align-items:start;}
  @media(max-width:900px){.csp-layout{grid-template-columns:1fr;}}
  .csp-toc{position:sticky;top:80px;}
  @media(max-width:900px){.csp-toc{display:none;}}
  .csp-toc-title{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#7c6b9e;margin-bottom:.75rem;}
  .csp-toc ul{list-style:none;padding:0;margin:0;}.csp-toc li{margin-bottom:.15rem;}
  .csp-toc button{background:none;border:none;cursor:pointer;text-align:left;width:100%;padding:.4rem .6rem;font-size:.82rem;color:#9ca3af;border-radius:6px;border-left:2px solid transparent;transition:all .15s;line-height:1.4;}
  .csp-toc button:hover{color:#e8deff;background:rgba(255,255,255,.05);}
  .csp-toc button.active{color:#d4af37;border-left-color:#d4af37;background:rgba(212,175,55,.07);font-weight:600;}
  .csp-section{margin-bottom:3.5rem;scroll-margin-top:90px;}
  .csp-h2{font-size:clamp(1.25rem,2.5vw,1.65rem);font-weight:700;color:#f0e6ff;margin:0 0 1.25rem;padding-bottom:.6rem;border-bottom:1px solid rgba(212,175,55,.2);}
  .csp-h3{font-size:1.02rem;font-weight:700;color:#d4af37;margin:1.25rem 0 .45rem;}
  .csp-p{font-size:.97rem;line-height:1.8;color:#c4b5fd;margin:0 0 1rem;}
  .csp-parent{display:inline-flex;align-items:center;gap:.4rem;font-size:.82rem;color:#7c6b9e;text-decoration:none;margin-bottom:.75rem;}
  .csp-parent:hover{color:#a78bfa;}
  .csp-bullets{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.65rem;}
  .csp-bullets li{display:flex;gap:.75rem;align-items:flex-start;font-size:.95rem;line-height:1.65;color:#c4b5fd;}
  .csp-bi{width:22px;height:22px;background:rgba(212,175,55,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.7rem;color:#d4af37;flex-shrink:0;margin-top:2px;}
  .csp-bs{color:#e8deff;font-weight:600;}
  .csp-before-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:600px){.csp-before-grid{grid-template-columns:1fr;}}
  .csp-before-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;display:flex;gap:.75rem;align-items:flex-start;transition:border-color .2s;}
  .csp-before-card:hover{border-color:rgba(212,175,55,.2);}
  .csp-before-icon{font-size:1.3rem;width:38px;height:38px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .csp-before-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .csp-before-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .csp-weeks{display:flex;flex-direction:column;gap:1.25rem;}
  .csp-week-card{border-radius:14px;padding:1.5rem;border:1.5px solid;}
  .csp-week-header{display:flex;align-items:center;gap:.85rem;margin-bottom:1rem;}
  .csp-week-icon{font-size:1.4rem;width:46px;height:46px;display:flex;align-items:center;justify-content:center;border-radius:10px;flex-shrink:0;background:rgba(255,255,255,.06);}
  .csp-week-label{font-size:.7rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:.2rem;}
  .csp-week-theme{font-size:1.1rem;font-weight:800;color:#f0e6ff;}
  .csp-week-items{list-style:none;padding:0;margin:0 0 1rem;display:flex;flex-direction:column;gap:.5rem;}
  .csp-week-items li{display:flex;gap:.65rem;align-items:flex-start;font-size:.88rem;line-height:1.6;color:#c4b5fd;}
  .csp-week-check{font-size:.8rem;margin-top:2px;flex-shrink:0;}
  .csp-week-goal{background:rgba(0,0,0,.2);border-radius:8px;padding:.65rem .85rem;font-size:.8rem;color:#9ca3af;border-left:2px solid;}
  .csp-week-goal strong{color:#e8deff;}
  .csp-timetable{width:100%;border-collapse:collapse;font-size:.9rem;}
  .csp-timetable thead tr{background:rgba(212,175,55,.12);}
  .csp-timetable th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;}
  .csp-timetable td{padding:.75rem 1.25rem;border:1px solid rgba(255,255,255,.06);vertical-align:middle;}
  .csp-timetable tbody tr:hover{background:rgba(212,175,55,.04);}
  .csp-time{font-size:.82rem;font-weight:700;color:#e8deff;white-space:nowrap;}
  .csp-act{font-weight:700;color:#f0e6ff;font-size:.9rem;}
  .csp-note{font-size:.8rem;color:#9ca3af;margin-top:2px;}
  .csp-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;display:inline-block;margin-right:6px;}
  .csp-subject-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}
  @media(max-width:640px){.csp-subject-grid{grid-template-columns:1fr;}}
  .csp-sub{background:rgba(124,58,237,.08);border:1px solid rgba(124,58,237,.2);border-radius:12px;padding:1.1rem 1.25rem;}
  .csp-sub h3{font-size:.93rem;font-weight:700;color:#a78bfa;margin:0 0 .5rem;}
  .csp-sub p{font-size:.85rem;line-height:1.65;color:#9ca3af;margin:0 0 .5rem;}
  .csp-sub ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.35rem;}
  .csp-sub ul li{font-size:.84rem;color:#9ca3af;line-height:1.55;}
  .csp-sub ul li::before{content:'▸ ';color:#7c3aed;font-size:.75rem;}
  .csp-sub ul li strong{color:#e8deff;}
  .csp-checklist{display:grid;grid-template-columns:repeat(2,1fr);gap:1rem;}
  @media(max-width:640px){.csp-checklist{grid-template-columns:1fr;}}
  .csp-chk-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:12px;padding:1.1rem;}
  .csp-chk-week{font-size:.72rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#7c6b9e;margin-bottom:.25rem;}
  .csp-chk-theme{font-size:.95rem;font-weight:700;color:#f0e6ff;margin-bottom:.75rem;}
  .csp-chk-item{display:flex;align-items:center;gap:.6rem;padding:.4rem 0;font-size:.85rem;color:#c4b5fd;border-bottom:1px solid rgba(255,255,255,.05);}
  .csp-chk-item:last-child{border-bottom:none;}
  .csp-chk-box{width:16px;height:16px;border:1.5px solid rgba(212,175,55,.4);border-radius:3px;flex-shrink:0;background:rgba(212,175,55,.06);}
  .csp-mistakes{display:flex;flex-direction:column;gap:.6rem;}
  .csp-mistake{display:flex;gap:.75rem;align-items:flex-start;background:rgba(239,68,68,.05);border:1px solid rgba(239,68,68,.15);border-radius:8px;padding:.85rem 1rem;}
  .csp-mx{color:#ef4444;font-size:1rem;flex-shrink:0;margin-top:1px;font-weight:700;}
  .csp-mistake p{font-size:.88rem;line-height:1.55;color:#c4b5fd;margin:0;}
  .csp-mistake strong{color:#f0e6ff;}
  .csp-res-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:600px){.csp-res-grid{grid-template-columns:1fr;}}
  .csp-res-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s;}
  .csp-res-card:hover{border-color:rgba(212,175,55,.25);}
  .csp-res-icon{font-size:1.4rem;margin-bottom:.5rem;}
  .csp-res-card h3{font-size:.9rem;font-weight:700;color:#f0e6ff;margin:0 0 .35rem;}
  .csp-res-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .csp-besst-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:640px){.csp-besst-grid{grid-template-columns:1fr;}}
  .csp-besst-card{display:flex;gap:.85rem;align-items:flex-start;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s,background .2s;}
  .csp-besst-card:hover{border-color:rgba(212,175,55,.25);background:rgba(255,255,255,.05);}
  .csp-besst-icon{font-size:1.4rem;width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .csp-besst-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .csp-besst-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .csp-cmp-wrap{overflow-x:auto;border-radius:12px;}
  .csp-cmp{width:100%;border-collapse:collapse;font-size:.9rem;}
  .csp-cmp thead tr{background:rgba(212,175,55,.12);}
  .csp-cmp th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;}
  .csp-cmp td{padding:.75rem 1.25rem;border:1px solid rgba(255,255,255,.06);color:#c4b5fd;}
  .csp-cmp tbody tr:nth-child(even){background:rgba(255,255,255,.025);}
  .csp-cmp tbody tr:hover{background:rgba(212,175,55,.05);}
  .csp-better{color:#22c55e;font-weight:600;}
  .csp-tips-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:600px){.csp-tips-grid{grid-template-columns:1fr;}}
  .csp-tip{display:flex;gap:.65rem;align-items:flex-start;background:rgba(34,197,94,.05);border:1px solid rgba(34,197,94,.15);border-radius:8px;padding:.85rem 1rem;font-size:.88rem;line-height:1.55;color:#c4b5fd;}
  .csp-tip-ck{color:#22c55e;flex-shrink:0;font-size:1rem;margin-top:1px;}
  .csp-faq-list{display:flex;flex-direction:column;gap:.6rem;}
  .csp-faq{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:10px;overflow:hidden;transition:border-color .2s;}
  .csp-faq.open{border-color:rgba(212,175,55,.3);}
  .csp-faq-q{width:100%;background:none;border:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 1.25rem;text-align:left;font-size:.93rem;font-weight:600;color:#e8deff;transition:color .15s;}
  .csp-faq-q:hover{color:#d4af37;}
  .csp-chev{font-size:.75rem;color:#7c6b9e;transition:transform .25s;flex-shrink:0;}
  .csp-faq.open .csp-chev{transform:rotate(180deg);color:#d4af37;}
  .csp-faq-a{max-height:0;overflow:hidden;transition:max-height .3s ease;}
  .csp-faq.open .csp-faq-a{max-height:500px;}
  .csp-faq-ai{padding:.85rem 1.25rem 1rem;font-size:.88rem;line-height:1.75;color:#9ca3af;border-top:1px solid rgba(255,255,255,.06);}
  .csp-links{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:540px){.csp-links{grid-template-columns:1fr;}}
  .csp-ilink{display:flex;align-items:center;gap:.6rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:8px;padding:.7rem 1rem;text-decoration:none;color:#a78bfa;font-size:.88rem;font-weight:500;transition:border-color .2s,background .2s;}
  .csp-ilink:hover{border-color:rgba(167,139,250,.4);background:rgba(167,139,250,.06);color:#c4b5fd;}
  .csp-cta{background:linear-gradient(135deg,rgba(124,58,237,.25) 0%,rgba(212,175,55,.12) 100%);border:1px solid rgba(212,175,55,.2);border-radius:16px;padding:2.5rem;text-align:center;margin-top:1rem;}
  .csp-cta h2{font-size:1.5rem;font-weight:800;color:#f0e6ff;margin:0 0 .75rem;}
  .csp-cta p{font-size:.95rem;line-height:1.7;color:#c4b5fd;max-width:600px;margin:0 auto 1.5rem;}
`;

const CuetStudyPlanPage = () => {
  const [activeSection, setActiveSection] = useState('why-need');
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    TOC.forEach(t => { const el = document.getElementById(t.id); if (el) observerRef.current.observe(el); });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Helmet>
        <title>CUET Study Plan: 30-Day Preparation Strategy to Crack CUET</title>
        <meta name="description" content="Follow a practical CUET study plan with a detailed 30-day preparation schedule, expert tips, and revision strategies. Prepare smarter with BESST." />
        <meta name="keywords" content="cuet study plan, 30 day cuet study plan, cuet preparation plan, cuet study timetable, cuet preparation schedule, how to prepare for cuet in 30 days, cuet preparation strategy, cuet study routine, cuet revision plan, daily study plan for cuet" />
        <meta property="og:title" content="CUET Study Plan: 30-Day Preparation Strategy to Crack CUET" />
        <meta property="og:description" content="Follow a practical CUET study plan with a detailed 30-day preparation schedule, expert tips, and revision strategies. Prepare smarter with BESST." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://besst.in/cuet-study-plan" />
        <link rel="canonical" href="https://besst.in/cuet-study-plan" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="csp-page mt-20">
        <style>{CSS}</style>

        <nav className="csp-bc" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span>›</span>
          <Link to="/cuet-coaching">CUET Coaching</Link><span>›</span>
          <span style={{ color: '#e8deff' }}>CUET Study Plan</span>
        </nav>

        <div className="csp-hero">
          <div className="csp-badge">30-Day Preparation Guide</div>
          <h1 className="csp-h1"><em>CUET Study Plan:</em> A Practical 30-Day Strategy to Prepare for CUET Successfully</h1>
          <p className="csp-intro">
            A structured study plan fixes the most common CUET preparation mistake — studying without a plan. This page walks through a practical 30-day CUET study plan, a sample daily timetable, and weekly checklists you can act on immediately.
          </p>
          <div className="csp-cta">
            <Link to="/subscriptions" className="btn-gold">Get a Personalised Study Plan →</Link>
            <Link to="/cuet-mock-test" className="btn-outline">Try a Free Mock Test</Link>
          </div>
        </div>

        <div className="csp-layout">
          <aside className="csp-toc" aria-label="Table of contents">
            <div className="csp-toc-title">On This Page</div>
            <ul>
              {TOC.map(t => (
                <li key={t.id}>
                  <button className={activeSection === t.id ? 'active' : ''} onClick={() => scrollTo(t.id)}>{t.label}</button>
                </li>
              ))}
            </ul>
          </aside>

          <main>
            <Link to="/cuet-coaching" className="csp-parent">← Back to CUET Coaching</Link>

            <div className="csp-qa">
              <div className="csp-qa-lbl">Quick Answer</div>
              <p className="csp-qa-txt">A good CUET study plan balances concept learning, daily practice, revision, and mock tests. A structured 30-day plan helps students cover the syllabus efficiently, improve time management, and identify weak areas before the examination — ideally following a foundation phase, a practice phase, an accuracy phase, and a final revision phase.</p>
            </div>

            {/* Why need */}
            <section id="why-need" className="csp-section">
              <h2 className="csp-h2">Why Do You Need a CUET Study Plan?</h2>
              <ul className="csp-bullets">
                {[
                  ['Better time management', 'A plan tells you exactly what to study and when, instead of deciding reactively each day.'],
                  ['Reduced exam stress', 'Knowing you are on track removes a large part of the anxiety that builds up closer to the exam.'],
                  ['Balanced preparation across subjects', 'A schedule prevents any one subject from being neglected until the final week.'],
                  ['Regular revision', 'Built into the plan rather than treated as an afterthought.'],
                  ['Improved consistency', 'A routine is far easier to sustain than motivation alone.'],
                  ['Better mock test performance', 'Structured preparation shows up directly in more consistent mock scores over time.'],
                ].map(([t, d]) => (
                  <li key={t}><span className="csp-bi">✓</span><span><span className="csp-bs">{t}: </span>{d}</span></li>
                ))}
              </ul>
            </section>

            {/* Before start */}
            <section id="before-start" className="csp-section">
              <h2 className="csp-h2">Before You Create Your CUET Study Plan</h2>
              <div className="csp-before-grid">
                {[
                  ['📋', 'Understand the Exam Pattern', "Know the sections, sequencing, and timing of CUET before building a schedule around it — the plan should match the exam, not the other way around."],
                  ['🔍', 'Analyse the Syllabus', 'Break the syllabus into topics so the plan can allocate time proportionally, rather than guessing how long each subject needs.'],
                  ['📊', 'Know Your Strengths & Weaknesses', 'An honest early assessment helps allocate more time to genuinely weak areas instead of spreading effort evenly.'],
                  ['🎯', 'Set Realistic Daily Goals', 'A plan with more targets than a day can reasonably hold gets abandoned within a week. Build in some slack.'],
                  ['📚', 'Gather the Right Resources', 'Have NCERT books, previous year papers, and practice material ready before Day 1, so the plan does not stall while resources are still being sourced.'],
                  ['⏰', 'Account for Board Exams', 'If board exams overlap, the daily timetable must reflect both commitments from the start, not be adjusted later.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="csp-before-card">
                    <div className="csp-before-icon">{icon}</div>
                    <div><h3>{title}</h3><p>{desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* 30-day plan */}
            <section id="thirty-day" className="csp-section">
              <h2 className="csp-h2">30-Day CUET Study Plan</h2>
              <p className="csp-p">Each week has a distinct focus — from building foundations to full exam readiness. A 30-day plan works well as the final phase; students starting earlier can stretch the same structure over 3–6 months for deeper concept coverage.</p>
              <div className="csp-weeks">
                {WEEKS.map(w => (
                  <div key={w.week} className="csp-week-card" style={{ background: w.bg, borderColor: w.border }}>
                    <div className="csp-week-header">
                      <div className="csp-week-icon">{w.icon}</div>
                      <div>
                        <div className="csp-week-label" style={{ color: w.color }}>{w.week}</div>
                        <div className="csp-week-theme">{w.theme}</div>
                      </div>
                    </div>
                    <ul className="csp-week-items">
                      {w.items.map(item => (
                        <li key={item}>
                          <span className="csp-week-check" style={{ color: w.color }}>✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="csp-week-goal" style={{ borderLeftColor: w.color }}>
                      <strong>Target: </strong>{w.goal}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Daily timetable */}
            <section id="daily-timetable" className="csp-section">
              <h2 className="csp-h2">Sample Daily CUET Study Timetable</h2>
              <p className="csp-p" style={{ marginBottom: '1rem' }}>This is a starting template, not a fixed rulebook — adjust slots around your school or coaching schedule, and shift mock test analysis to whichever day you actually take a test that week.</p>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '.85rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {[['revision','Revision'],['study','Subject Study'],['practice','Practice'],['analysis','Analysis'],['break','Break']].map(([type, label]) => (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.78rem', color: '#9ca3af' }}>
                    <span className="csp-dot" style={{ background: BLOCK_COLORS[type].dot }} />
                    {label}
                  </div>
                ))}
              </div>

              <div style={{ overflowX: 'auto', borderRadius: 12 }}>
                <table className="csp-timetable">
                  <thead><tr><th>Time</th><th>Activity</th><th>Notes</th></tr></thead>
                  <tbody>
                    {TIMETABLE.map(row => {
                      const c = BLOCK_COLORS[row.type];
                      return (
                        <tr key={row.time} style={{ background: c.bg }}>
                          <td style={{ borderLeft: `3px solid ${c.dot}` }}>
                            <span className="csp-time">{row.time}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                              <span className="csp-dot" style={{ background: c.dot }} />
                              <span className="csp-act">{row.activity}</span>
                            </div>
                          </td>
                          <td><span className="csp-note">{row.note}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Subject-wise */}
            <section id="subject-wise" className="csp-section">
              <h2 className="csp-h2">Subject-wise Study Plan</h2>
              <div className="csp-subject-grid">
                <div className="csp-sub">
                  <h3>📖 Language Section</h3>
                  <p>Build daily reading and comprehension practice into the routine, and use a portion of each week for vocabulary building rather than cramming it into a single session.</p>
                </div>
                <div className="csp-sub">
                  <h3>📚 Domain Subjects</h3>
                  <p>Use Weeks 1–2 to build NCERT-based concepts, then shift to topic-wise testing in Weeks 2–3 so concepts get reinforced before the accuracy phase begins.</p>
                </div>
                <div className="csp-sub" style={{ gridColumn: '1 / -1' }}>
                  <h3>🧠 General Test</h3>
                  <ul>
                    <li><strong>Quantitative Aptitude:</strong> Practise daily in short sets rather than occasional long sessions, to build speed steadily</li>
                    <li><strong>Logical Reasoning:</strong> Rotate through different question types across the week rather than repeating the same pattern</li>
                    <li><strong>General Knowledge:</strong> Set aside 15–20 minutes daily for review rather than a single weekly block</li>
                    <li><strong>Current Affairs:</strong> Follow updates consistently through all four weeks — this section cannot be completed early like the others</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Weekly goals checklist */}
            <section id="weekly-goals" className="csp-section">
              <h2 className="csp-h2">Weekly Goals to Stay on Track</h2>
              <div className="csp-checklist">
                {[
                  { week: 'Week 1', theme: 'Foundation', items: ['Complete syllabus analysis', 'Create structured notes', 'Build NCERT concepts'] },
                  { week: 'Week 2', theme: 'Practice', items: ['Topic-wise practice complete', 'Solve first PYQ set', 'Revise daily without skipping'] },
                  { week: 'Week 3', theme: 'Accuracy', items: ['Sectional mock tests done', 'Performance analysis complete', 'Weak areas actively improved'] },
                  { week: 'Week 4', theme: 'Exam Ready', items: ['2–3 full-length mocks complete', 'Final revision done', 'Exam strategy confirmed'] },
                ].map(({ week, theme, items }) => (
                  <div key={week} className="csp-chk-card">
                    <div className="csp-chk-week">{week}</div>
                    <div className="csp-chk-theme">{theme}</div>
                    {items.map(item => (
                      <div key={item} className="csp-chk-item">
                        <div className="csp-chk-box" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>

            {/* Mistakes */}
            <section id="mistakes" className="csp-section">
              <h2 className="csp-h2">Common Mistakes Students Make While Following a Study Plan</h2>
              <div className="csp-mistakes">
                {[
                  ['Studying without goals', 'Sitting down to "study" without a specific target for the session — a plan needs daily targets, not just weekly ones.'],
                  ['Ignoring revision', 'Treating the first pass through a topic as sufficient — revision is what makes concepts stick, not just initial study.'],
                  ['Not taking breaks', 'Leading to burnout well before the plan\'s final week — rest is built into the sample timetable for a reason.'],
                  ['Skipping mock tests', 'The single most common reason a study plan does not translate into exam performance.'],
                  ['Studying only favourite subjects', 'Leaving weaker areas under-practised right up to the exam.'],
                  ['Not analysing mistakes', 'Repeating the same errors across multiple mock tests because the root cause was never examined.'],
                  ['Following unrealistic schedules', 'A plan so packed it collapses within the first week — realistic daily targets are non-negotiable.'],
                ].map(([t, d]) => (
                  <div key={t} className="csp-mistake">
                    <span className="csp-mx">✕</span>
                    <p><strong>{t}: </strong>{d}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Resources */}
            <section id="resources" className="csp-section">
              <h2 className="csp-h2">Study Resources You Should Include</h2>
              <div className="csp-res-grid">
                {[
                  ['📚', 'NCERT Books', 'The core resource for building concepts across most domain subjects.'],
                  ['📄', 'Previous Year Papers', "Essential for understanding NTA's actual question style and recurring topics."],
                  ['📝', 'Mock Tests', 'The closest available simulation of real exam conditions, and the backbone of Weeks 3 and 4.'],
                  ['✏️', 'Sample Papers', 'Useful additional practice once NCERT concepts and PYQ patterns are reasonably well understood.'],
                  ['🌐', 'Online Learning Platforms', "A structured platform turns this whole resource list into one coherent, scheduled plan — this is where BESST's study materials and mock test series slot directly into the plan above."],
                  ['📊', 'Performance Analytics', 'Turning Week 3 mock results into a specific action plan for the final week.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="csp-res-card">
                    <div className="csp-res-icon">{icon}</div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* How BESST helps */}
            <section id="besst-helps" className="csp-section">
              <h2 className="csp-h2">How BESST Helps Students Follow a Structured CUET Study Plan</h2>
              <div className="csp-besst-grid">
                {BESST_FEATURES.map(f => (
                  <div key={f.title} className="csp-besst-card">
                    <div className="csp-besst-icon">{f.icon}</div>
                    <div><h3>{f.title}</h3><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
                <Link to="/online-cuet-coaching" className="btn-gold">Follow a Structured Plan with BESST →</Link>
              </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="csp-section">
              <h2 className="csp-h2">Self-Study Plan vs Guided Online Coaching</h2>
              <div className="csp-cmp-wrap">
                <table className="csp-cmp">
                  <thead><tr><th>Factors</th><th>Self Study</th><th>BESST Online Coaching</th></tr></thead>
                  <tbody>
                    {[
                      ['Study Plan', 'Self-created', 'Expert-designed'],
                      ['Daily Accountability', 'Limited', '✔'],
                      ['Mock Tests', 'Limited', '✔'],
                      ['Faculty Support', '✘', '✔'],
                      ['Mentorship', '✘', '✔'],
                      ['Performance Tracking', '✘', '✔'],
                      ['Revision Schedule', 'Self-managed', 'Structured'],
                    ].map(([f, self, besst]) => (
                      <tr key={f}>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{f}</td>
                        <td style={{ color: '#9ca3af' }}>{self}</td>
                        <td className="csp-better">{besst}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="csp-p" style={{ marginTop: '1rem' }}>A self-created study plan works well for students confident in building and sticking to their own schedule. Where guided coaching tends to add the most value is accountability and adjustment — an expert-designed plan that gets refined based on actual mock test performance.</p>
            </section>

            {/* Expert tips */}
            <section id="expert-tips" className="csp-section">
              <h2 className="csp-h2">Expert Tips to Stick to Your CUET Study Plan</h2>
              <div className="csp-tips-grid">
                {[
                  'Set achievable daily targets rather than aspirational ones',
                  'Revise every day, even briefly, rather than saving it for a weekly block',
                  'Take one mock test every week once you are past the foundation phase',
                  'Avoid multitasking — a focused hour outperforms three distracted ones',
                  'Track your progress so you can see the plan actually working',
                  'Stay consistent instead of studying for long hours occasionally',
                  'Prioritise sleep and well-being — burnout undoes weeks of consistent progress',
                ].map(tip => (
                  <div key={tip} className="csp-tip"><span className="csp-tip-ck">✓</span><span>{tip}</span></div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="csp-section">
              <h2 className="csp-h2">Frequently Asked Questions (FAQs)</h2>
              <div className="csp-faq-list">
                {FAQS.map((f, i) => (
                  <div key={i} className={`csp-faq ${openFaq === i ? 'open' : ''}`}>
                    <button className="csp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                      <span>{f.q}</span><span className="csp-chev">▼</span>
                    </button>
                    <div className="csp-faq-a" aria-hidden={openFaq !== i}>
                      <div className="csp-faq-ai">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Internal links */}
            <section className="csp-section">
              <h2 className="csp-h2">Explore More CUET Resources</h2>
              <div className="csp-links">
                {[
                  ['/cuet-coaching', '📚 CUET Coaching (Pillar)'],
                  ['/online-cuet-coaching', '💻 Online CUET Coaching'],
                  ['/cuet-preparation-strategy', '🎯 CUET Preparation Strategy'],
                  ['/how-to-crack-cuet', '🏆 How to Crack CUET'],
                  ['/cuet-mock-test', '📝 CUET Mock Test'],
                  ['/cuet-mock-test/free', '🆓 Free CUET Mock Test'],
                  ['/cuet-previous-year-question-papers', '📄 CUET PYQ Papers'],
                  ['/cuet-syllabus', '📋 CUET Syllabus'],
                  ['/cuet-exam-pattern', '📐 CUET Exam Pattern'],
                  ['/cuet-registration', '📝 CUET Registration'],
                  ['/cuet-subject-list', '📑 CUET Subject List'],
                  ['/cuet-cutoff', '📊 CUET Cutoff'],
                  ['/cuet-counselling', '🎓 CUET Counselling'],
                ].map(([to, label]) => (
                  <Link key={to} to={to} className="csp-ilink">
                    <span>{label}</span>
                    <span style={{ marginLeft: 'auto', opacity: .5 }}>→</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="csp-cta">
              <h2>Ready to Follow a Structured CUET Study Plan?</h2>
              <p>
                A good study plan isn't about perfection — it's about consistency. BESST's CUET Online Coaching provides expert-designed study plans, live interactive classes, comprehensive study materials, regular mock tests, and personalised mentorship to help you stay on track.
              </p>
              <Link to="/subscriptions" className="btn-gold">Get Your Personalised CUET Study Plan →</Link>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default CuetStudyPlanPage;
